import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Form,
  Input,
  Button,
  Collapse,
  Card,
  Divider,
  Typography,
  DatePicker,
  Table,
  Radio,
  Checkbox,
  Select,
  message,
  Upload,
  Spin,
  Modal,
} from "antd";
import {
  EyeOutlined,
  UploadOutlined,
  CloudUploadOutlined,
  CameraOutlined,
} from "@ant-design/icons";
import { PlusOutlined } from "@ant-design/icons";
import {
  FaUser,
  FaUserTie,
  FaCalendarAlt,
  FaEnvelope,
  FaHome,
  FaIdCard,
  FaBuilding,
  FaFileAlt,
  FaCaretRight,
  FaCheck,
  FaBriefcase,
} from "react-icons/fa";
import moment from "moment";
import { useAuth } from "../../../context/AuthContext";
import styled from "styled-components";
import { trackFeatureAction } from "../../../analytics/googleAnalytics";
import { useDispatch, useSelector } from "react-redux";
import {
  EmployeesContainer,
  MainCard,
  CustomCollapse,
  PanelHeader,
  PanelIcon,
  SectionForm,
  FormDivider,
  FormInput,
  IconInput,
  IconTextArea,
  SubmitButton,
  NameSection,
  AddressSection,
  StyledTable,
  UploadContainer,
} from "../styledComp";
import {
  postESIInfo,
  getESIInfo,
  clearESIData,
  uploadPhotograph,
  updateSectionStatus
} from "../../../redux/slice/hrFormSlice";
import PdfESI from "./PdfESI";

const ESI_SECTION_NAMES = {
        1: "Person's Particulars",
        2: "Employer Details",
        3: "Nominee Details",
        4: "Family Particulars",
        5: "Temporary Identity Card",
      };

const { Title, Text } = Typography;
const { Panel } = Collapse;
const { Option } = Select;

const ESIForm = forwardRef(
  ({ user_id, searchTrigger, jwt_payload_group }, ref) => {
    if (user_id === null) return null;

    const [activeKey, setActiveKey] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [familyData, setFamilyData] = useState(
      Array.from({ length: 7 }, (_, i) => ({
        key: i,
        sno: i + 1,
        name: i === 0 ? "" : "",
        dob: i === 0 ? "" : "",
        relationship: i === 0 ? "" : "",
        customRelationship: "",
        residing: i === 0 ? "" : "",
        town: "",
        state: "",
        isDependent: false,
      }))
    );
    const [sameAsCurrent, setSameAsCurrent] = useState(false);
    const [maritalStatus, setMaritalStatus] = useState(null);
    const [parentsDeceased, setParentsDeceased] = useState(false);
    const [photoUploaded, setPhotoUploaded] = useState(false);

    const { authState } = useAuth();
    const dispatch = useDispatch();
    const jwtBearer = authState.token;
    const mainData = useSelector((state) => state?.hrForm?.esi_data || {});

    const [section1Form] = Form.useForm();
    const [section2Form] = Form.useForm();
    const [section3Form] = Form.useForm();
    const [section4Form] = Form.useForm();
    const [section5Form] = Form.useForm();

    useImperativeHandle(ref, () => ({
      resetAllForms: () => {
        section1Form.resetFields();
        section2Form.resetFields();
        section3Form.resetFields();
        section4Form.resetFields();
        section5Form.resetFields();

        setFamilyData(
          Array.from({ length: 7 }, (_, i) => ({
            key: i,
            sno: i + 1,
            name: i === 0 ? "" : "",
            dob: i === 0 ? "" : "",
            relationship: i === 0 ? "" : "",
            residing: i === 0 ? "" : "",
            town: "",
            state: "",
            isDependent: false,
          }))
        );

        setSameAsCurrent(false);
        setMaritalStatus(null);
        setParentsDeceased(false);
      },
    }));


    useEffect(() => {
      trackFeatureAction(
        "MainHrForm",
        "ESI Form",
        "open"
      );
    }, []);

    useEffect(() => {
      if (user_id === null) {
        dispatch(clearESIData());
      } else {
        dispatch(getESIInfo({ jwtBearer, user_id }));
      }
    }, [dispatch, jwtBearer, user_id, searchTrigger]);

    useEffect(() => {
      if (mainData) {
        const section1Data = mainData.section1 || {};
        const currentMaritalStatus = section1Data.maritalStatus;

        section1Form.setFieldsValue({
          ...section1Data,
          dob: section1Data.dob ? moment(section1Data.dob) : null,
        });

        // Set Section 2 values
        section2Form.setFieldsValue({
          ...mainData.section2,
          appointmentDate: mainData.section2?.appointmentDate 
            ? moment(mainData.section2.appointmentDate) 
            : null,
        });

        section3Form.setFieldsValue({
          ...mainData.section3,
        });

        section4Form.setFieldsValue({
          ...mainData.section4,
        });

        section5Form.setFieldsValue({
          ...mainData.section5,
          entryDate: mainData.section5?.entryDate
            ? moment(mainData.section5.entryDate)
            : null,
          cardValidity: mainData.section5?.cardValidity
            ? moment(mainData.section5.cardValidity)
            : null,
          cardDated: mainData.section5?.cardDated
            ? moment(mainData.section5.cardDated)
            : null,
        });

        if (mainData.section4?.familyData) {
          setFamilyData(
            familyData.map((item, index) => ({
              ...item,
              ...(mainData.section4.familyData[index] || {}),
              dob: mainData.section4.familyData[index]?.dob
                ? moment(mainData.section4.familyData[index].dob)
                : null,
              isDependent:
                mainData.section4.familyData[index]?.isDependent || false,
            }))
          );
        }

        if (currentMaritalStatus) {
          setMaritalStatus(currentMaritalStatus);
        }
      }
    }, [mainData]);

    useEffect(() => {
      if (sameAsCurrent) {
        const presentAddress = section1Form.getFieldValue("presentAddress");
        const presentPin = section1Form.getFieldValue("presentPin");
        const email = section1Form.getFieldValue("email");

        section1Form.setFieldsValue({
          permanentAddress: presentAddress,
          permanentPin: presentPin,
          permanentEmail: email,
        });
      }
    }, [sameAsCurrent, section1Form]);

    const handleMaritalStatusChange = (e) => {
      const status = e.target.value;
      setMaritalStatus(status);

      section3Form.setFieldsValue({
        nomineeRelationship: undefined,
      });

      setParentsDeceased(false);
    };

    const getRelationshipOptions = () => {
      if (!maritalStatus) return [];

      if (maritalStatus === "U" || maritalStatus === "W") {
        return parentsDeceased
          ? [
              { value: "brother", label: "Brother" },
              { value: "sister", label: "Sister" },
            ]
          : [
              { value: "father", label: "Father" },
              { value: "mother", label: "Mother" },
            ];
      } else {
        return [
          { value: "husband", label: "Husband" },
          { value: "wife", label: "Wife" },
          { value: "father", label: "Father (Dependent)" },
          { value: "mother", label: "Mother (Dependent)" },
        ];
      }
    };

    const isSectionCompleted = (sectionNumber) => {
      return mainData[`is_section${sectionNumber}_completed`] === true;
    };

    const isAllSectionsCompleted = () => {
      return [1, 2, 3, 4, 5].every(
        (section) =>
          isSectionCompleted(section) &&
          !(jwt_payload_group === "10" || jwt_payload_group === "14" || jwt_payload_group === "18")
      );
    };

    const renderField = (sectionNumber, element) => {
      if (isAllSectionsCompleted()) {
        return React.cloneElement(element, {
          disabled: true,
          ...element.props,
        });
      }
      return React.cloneElement(element, {
        disabled: false,
        ...element.props,
      });
    };

    const onCollapseChange = (keys) => {
      setActiveKey(keys);
    };

    const handleSectionSubmit = async (sectionNumber, values) => {
      let sectionData = {};

      switch (sectionNumber) {
        case 1:
          sectionData = {
            insuranceNo: values.insuranceNo,
            name: values.name,
            fatherHusbandName: values.fatherHusbandName,
            dob: values.dob,
            maritalStatus: values.maritalStatus,
            gender: values.gender,
            presentAddress: values.presentAddress,
            presentPin: values.presentPin,
            email: values.email,
            permanentAddress: values.permanentAddress,
            permanentPin: values.permanentPin,
            permanentEmail: values.permanentEmail,
            branchOffice: values.branchOffice,
            dispensary: values.dispensary,
            nameAddressEmployer: values.nameAddressEmployer
          };
          break;
          
        case 2: // New Section 2: Employer Details
          sectionData = {
            employerCode: values.employerCode,
            appointmentDate: values.appointmentDate,
            employerName: values.employerName,
            employerAddress: values.employerAddress,
            previousEmployerCode: values.previousEmployerCode,
            previousInsNo: values.previousInsNo
          };
          break;
          
        case 3:
          const nomineeRelationship = values.nomineeRelationship;
          if (
            maritalStatus === "U" &&
            !parentsDeceased &&
            !["father", "mother"].includes(nomineeRelationship)
          ) {
            message.error(
              "Unmarried employees can only nominate parents unless parents are deceased"
            );
            return;
          }
          if (
            maritalStatus === "U" &&
            parentsDeceased &&
            !["brother", "sister"].includes(nomineeRelationship)
          ) {
            message.error(
              "Unmarried employees with deceased parents can only nominate siblings"
            );
            return;
          }
          if (
            maritalStatus !== "U" &&
            ["father", "mother"].includes(nomineeRelationship) &&
            !values.isDependent
          ) {
            message.error(
              "Married employees can only nominate parents if they are dependent"
            );
            return;
          }

          sectionData = {
            nomineeName: values.nomineeName,
            nomineeRelationship: values.nomineeRelationship,
            nomineeAddress: values.nomineeAddress,
            declaration: values.declaration,
            isDependent: values.isDependent,
            parentsDeceased: parentsDeceased,
          };
          break;
          
        case 4:
          if (
            !familyData[0].name ||
            !familyData[0].dob ||
            !familyData[0].relationship ||
            !familyData[0].residing
          ) {
            message.error(
              "Please fill all mandatory fields for Family Member 1"
            );
            return;
          }

          const invalidSibling = familyData.find((member) => {
            if (
              ["brother", "sister"].includes(member.relationship) &&
              member.dob
            ) {
              const age = moment().diff(moment(member.dob), "years");
              return age >= 24;
            }
            return false;
          });

          if (invalidSibling) {
            message.error("Siblings must be under 24 years old");
            return;
          }

          if (maritalStatus === "U") {
            const invalidMember = familyData.find(
              (member) =>
                member.name &&
                !["father", "mother", "brother", "sister", "other"].includes(
                  member.relationship
                )
            );
            if (invalidMember) {
              message.error(
                "Unmarried employees can only add parents or siblings as family members"
              );
              return;
            }
          }

          sectionData = {
            familyData: familyData,
          };
          break;
          
        case 5:
          sectionData = {
            cardName: values.cardName,
            entryDate: values.entryDate,
            cardInsNo: values.cardInsNo,
            cardFatherHusbandName: values.fatherHusbandName,
            cardBranchOffice: values.cardBranchOffice,
            cardDispensary: values.cardDispensary,
            cardValidity: values.cardValidity,
            cardDated: values.cardDated,
          };
          break;
        default:
          break;
      }

      const requestBody = {
        [`is_section${sectionNumber}_completed`]: true,
        [`section${sectionNumber}`]: sectionData,
      };

      try {
        const response = await dispatch(
          postESIInfo({
            jwtBearer,
            requestBody,
            section: sectionNumber,
            user_id,
          })
        );

        if (response.error) {
          throw new Error(
            `Failed to save section ${sectionNumber} information`
          );
        }

        await dispatch(getESIInfo({ jwtBearer, user_id }));
        trackFeatureAction(
          "MainHrForm",
          "ESI Form",
          "save",
          {
            section:
              ESI_SECTION_NAMES[sectionNumber] ||
              `section_${sectionNumber}`,
          }
        );

        message.success(
          `Section ${sectionNumber} information saved successfully`
        );
      } catch (error) {
        message.error(`Error saving section ${sectionNumber} information`);
        console.error("Error:", error);
      }
    };

    const handleFamilyChange = (key, field, value) => {
      if (isAllSectionsCompleted()) return;

      const newData = familyData.map((item) => {
        if (item.key === key) {
          return { ...item, [field]: value };
        }
        return item;
      });
      setFamilyData(newData);
    };

    const disabledDate = (current) => {
      return current && current > moment().endOf("day");
    };

    const familyColumns = [
      {
        title: "Sl. No.",
        dataIndex: "sno",
        key: "sno",
        width: 80,
        align: "center",
      },
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        render: (_, record) => (
          <Input
            value={record.name}
            onChange={(e) =>
              handleFamilyChange(record.key, "name", e.target.value)
            }
            placeholder="Name"
            disabled={isAllSectionsCompleted()}
            required={record.sno === 1}
          />
        ),
      },
      {
        title: "Date of Birth",
        dataIndex: "dob",
        key: "dob",
        render: (_, record) => (
          <DatePicker
            value={record.dob}
            onChange={(date) => handleFamilyChange(record.key, "dob", date)}
            format="DD/MM/YYYY"
            style={{ width: "100%" }}
            disabled={isAllSectionsCompleted()}
            disabledDate={disabledDate}
            required={record.sno === 1}
          />
        ),
      },
      {
        title: "Relationship with insured person",
        dataIndex: "relationship",
        key: "relationship",
        render: (_, record) => (
          <>
            <Select
              value={record.relationship}
              onChange={(value) =>
                handleFamilyChange(record.key, "relationship", value)
              }
              style={{ width: "100%" }}
              disabled={isAllSectionsCompleted()}
              placeholder="Select relationship"
            >
              {maritalStatus === "U" ? (
                parentsDeceased ? (
                  <>
                    <Option value="brother">Brother</Option>
                    <Option value="sister">Sister</Option>
                    <Option value="other">Other</Option>
                  </>
                ) : (
                  <>
                    <Option value="father">Father</Option>
                    <Option value="mother">Mother</Option>
                    <Option value="other">Other</Option>
                  </>
                )
              ) : (
                <>
                  {maritalStatus !== "W" && (
                    <>
                      <Option value="husband">Husband</Option>
                      <Option value="wife">Wife</Option>
                    </>
                  )}
                  <Option value="father">Father</Option>
                  <Option value="mother">Mother</Option>
                  <Option value="son">Son</Option>
                  <Option value="daughter">Daughter</Option>
                  <Option value="other">Other</Option>
                </>
              )}
            </Select>
            {record.relationship === "other" && (
              <Form.Item style={{ marginTop: 8 }}>
                <Input
                  value={record.customRelationship || ""}
                  onChange={(e) =>
                    handleFamilyChange(
                      record.key,
                      "customRelationship",
                      e.target.value
                    )
                  }
                  placeholder="Specify relationship"
                  disabled={isAllSectionsCompleted()}
                />
              </Form.Item>
            )}
          </>
        ),
      },
      {
        title: "Whether residing with him/her or not",
        dataIndex: "residing",
        key: "residing",
        render: (_, record) => (
          <Radio.Group
            value={record.residing}
            onChange={(e) =>
              handleFamilyChange(record.key, "residing", e.target.value)
            }
            disabled={isAllSectionsCompleted()}
            required={record.sno === 1}
          >
            <Radio value="yes">YES</Radio>
            <Radio value="no">NO</Radio>
          </Radio.Group>
        ),
      },
      {
        title: "If No, State place of Residence",
        key: "residencePlace",
        children: [
          {
            title: "TOWN",
            dataIndex: "town",
            key: "town",
            width: 150,
            render: (_, record) => (
              <Input
                value={record.town}
                onChange={(e) =>
                  handleFamilyChange(record.key, "town", e.target.value)
                }
                placeholder="Town"
                disabled={isAllSectionsCompleted() || record.residing === "yes"}
              />
            ),
          },
          {
            title: "STATE",
            dataIndex: "state",
            key: "state",
            width: 150,
            render: (_, record) => (
              <Input
                value={record.state}
                onChange={(e) =>
                  handleFamilyChange(record.key, "state", e.target.value)
                }
                placeholder="State"
                disabled={isAllSectionsCompleted() || record.residing === "yes"}
              />
            ),
          },
        ],
      },
    ];

    const [previewImage, setPreviewImage] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [showPreviewModal, setShowPreviewModal] = useState(false);

    const PreviewModal = ({ visible, image, onCancel }) => (
      <Modal
        visible={visible}
        title="Photo Preview"
        footer={null}
        onCancel={onCancel}
        width={1400}
      >
        <div style={{ textAlign: "center" }}>
          <img
            src={image}
            alt="preview"
            style={{ maxWidth: "100%", maxHeight: "400px" }}
          />
        </div>
      </Modal>
    );

    const handleSection5Submit = async (values) => {
        if (!mainData?.photograph && !photoUploaded) {
            message.error('Please upload a photograph before submitting');
            return;
        }
        await handleSectionSubmit(5, values);
    };

    const normFile = (e) => {
      if (Array.isArray(e)) {
        return e;
      }
      return e && e.fileList;
    };

    // const handleUpload = async () => {
    //     if (!selectedFile) return;
    
    //     const isImage = selectedFile.type.startsWith('image/');
    //     if (!isImage) {
    //         message.error('You can only upload image files!');
    //         return;
    //     }
    
    //     const isLt2M = selectedFile.size / 1024 / 1024 < 2;
    //     if (!isLt2M) {
    //         message.error('Image must be smaller than 2MB!');
    //         return;
    //     }
    
    //     setUploading(true);
    //     try {
    //         const resultAction = await dispatch(uploadPhotograph({
    //             user_id,
    //             file: selectedFile
    //         }));
    
    //         if (uploadPhotograph.fulfilled.match(resultAction)) {
    //             message.success('Photograph uploaded successfully!');
    //             dispatch(getESIInfo({ jwtBearer, user_id }));
    //             setSelectedFile(null);
    //             setPreviewImage('');
    //             setPhotoUploaded(true); 
    //         } else {
    //             message.error(resultAction.payload || 'Upload failed');
    //         }
    //     } catch (error) {
    //         message.error('Upload failed');
    //     } finally {
    //         setUploading(false);
    //     }
    // };

    const handleUpload = async () => {
    if (!selectedFile) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/jfif'];
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'jfif'];
    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    
    const isValidType = allowedTypes.includes(selectedFile.type) || 
                        allowedExtensions.includes(fileExtension);
    
    if (!isValidType) {
        message.error('You can only upload JPG, JPEG, PNG, or JFIF files!');
        return;
    }

    const isLt2M = selectedFile.size / 1024 / 1024 < 2;
    if (!isLt2M) {
        message.error('Image must be smaller than 2MB!');
        return;
    }

    setUploading(true);
    try {
        const resultAction = await dispatch(uploadPhotograph({
            user_id,
            file: selectedFile
        }));

        if (uploadPhotograph.fulfilled.match(resultAction)) {
          trackFeatureAction(
            "MainHrForm",
            "ESI Form",
            "upload",
            {
              document: "photograph",
            }
          );
            message.success('Photograph uploaded successfully!');
            dispatch(getESIInfo({ jwtBearer, user_id }));
            setSelectedFile(null);
            setPreviewImage('');
            setPhotoUploaded(true); 
        } else {
            message.error(resultAction.payload || 'Upload failed');
        }
    } catch (error) {
        message.error('Upload failed');
    } finally {
        setUploading(false);
    }
};
    const ResetSectionsButton = ({ type, jwtBearer, user_id }) => {
      const dispatch = useDispatch();
      const [loading, setLoading] = useState(false);
    
      const handleResetSections = async () => {
        try {
          setLoading(true);
          
          const updates =  {
                is_section1_completed: false,
                is_section2_completed: false,
                is_section3_completed: false,
                is_section4_completed: false,
                is_section5_completed: false
              }
           
              const resultAction = await dispatch(
            updateSectionStatus({
              jwtBearer,
              user_id,
              type,
              updates
            })
          );
    
          if (updateSectionStatus.fulfilled.match(resultAction)) {
            trackFeatureAction(
              "MainHrForm",
              "ESI Form",
              "reset"
            );
            message.success(`All sections reset successfully`);
            
              await dispatch(getESIInfo({ jwtBearer, user_id }));
          
          } else {
            throw new Error(resultAction.payload?.message || 'Failed to reset sections');
          }
        } catch (error) {
          message.error(error.message || 'Error resetting sections');
        } finally {
          setLoading(false);
        }
      };
    
      return (
        <Button 
          type="primary" 
          danger
          onClick={handleResetSections}
          loading={loading}
          style={{ marginLeft: 'auto' }}
        >
          Reset All Sections
        </Button>
      );
    };

    return (
      <EmployeesContainer>
        <MainCard
          title={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <span style={{ margin: 0 }}>ESI Declaration Form</span>

         <div style={{ display: "flex", justifyContent: "space-between",gap: "18px",alignItems: "center" }}>
      
              <PdfESI
                formData={mainData}
                jwt_payload_group={jwt_payload_group}
                jwtBearer={jwtBearer}
              />

{(jwt_payload_group === '10' || jwt_payload_group === '14' || jwt_payload_group === '18') && <ResetSectionsButton 
          type="esi" 
          jwtBearer={jwtBearer} 
          user_id={user_id} 
        />}
         </div>
              
            </div>
          }
          bordered={false}
        >
          <CustomCollapse
            accordion
            activeKey={activeKey}
            onChange={onCollapseChange}
            expandIcon={({ isActive }) => (
              <FaCaretRight
                style={{
                  transform: `rotate(${isActive ? 90 : 0}deg)`,
                  transition: "transform 0.3s",
                }}
              />
            )}
          >
            {/* Section 1: Insured Person's Particulars */}
            <Panel
              header={
                <PanelHeader>
                  <PanelIcon>
                    <FaUser />
                  </PanelIcon>
                  <span>Insured Person's Particulars</span>
                  {isSectionCompleted(1) && (
                    <Text type="success" style={{ marginLeft: "auto" }}>
                      Completed
                    </Text>
                  )}
                </PanelHeader>
              }
              key="1"
            >
              <SectionForm
                form={section1Form}
                layout="vertical"
                onFinish={(values) => handleSectionSubmit(1, values)}
                autoComplete="off"
              >
                <FormDivider orientation="left" orientationMargin="0">
                  Basic Information
                </FormDivider>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                  }}
                >
                  <Form.Item
                    label="1. Insurance No."
                    name="insuranceNo"
                    rules={[
                      {
                        pattern: /^[A-Za-z0-9\/\-]+$/,
                        message:
                          "Please enter a valid insurance number (alphanumeric with / or -)",
                      },
                    ]}
                  >
                    {renderField(
                      1,
                      <IconInput
                        placeholder="Insurance No."
                        prefix={<FaIdCard />}
                       
                      />
                    )}
                  </Form.Item>

                  <Form.Item
                    label="2. Name (in block letters) (As Per Aadhaar)"
                    name="name"
                    rules={[
                      { required: true, message: "Please enter your name" },
                      {
                        pattern: "^[A-Z ]+$",
                        message: "Please enter name in block letters!",
                      },
                    ]}
                  >
                    {renderField(
                      1,
                      <IconInput
                        placeholder="Full Name (As Per Aadhaar)"
                        prefix={<FaUser />}
                      />
                    )}
                  </Form.Item>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                  }}
                >
                  <Form.Item
                    label="3. Father's/Husband's Name (As Per Aadhaar)"
                    name="fatherHusbandName"
                    rules={[
                      {
                        required: true,
                        message: "Please enter father/husband name",
                      },
                      {
                        pattern: /^[A-Za-z\s.\-']+$/,
                        message:
                          "Please enter a valid name (only letters, spaces, dots, hyphens and apostrophes)",
                      },
                    ]}
                  >
                    {renderField(
                      1,
                      <IconInput
                        placeholder="Father's/Husband's Name (As Per Aadhaar)"
                        prefix={<FaUserTie />}
                      />
                    )}
                  </Form.Item>

                  <Form.Item
                    label="4. Date of Birth"
                    name="dob"
                    rules={[
                      {
                        required: true,
                        message: "Please select date of birth",
                      },
                    ]}
                  >
                    {renderField(
                      1,
                      <DatePicker
                        style={{ width: "100%" }}
                        format="DD/MM/YYYY"
                        placeholder="DD/MM/YYYY"
                        disabledDate={disabledDate}
                      />
                    )}
                  </Form.Item>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                  }}
                >
                  <Form.Item
                    label="5. Marital Status"
                    name="maritalStatus"
                    rules={[
                      {
                        required: true,
                        message: "Please select marital status",
                      },
                    ]}
                  >
                    {renderField(
                      1,
                      <Radio.Group onChange={handleMaritalStatusChange}>
                        <Radio value="M">Married</Radio>
                        <Radio value="U">Unmarried</Radio>
                        <Radio value="W">Widow/Widower</Radio>
                      </Radio.Group>
                    )}
                  </Form.Item>

                  <Form.Item
                    label="6. Sex"
                    name="gender"
                    rules={[
                      { required: true, message: "Please select gender" },
                    ]}
                  >
                    {renderField(
                      1,
                      <Radio.Group>
                        <Radio value="M">Male</Radio>
                        <Radio value="F">Female</Radio>
                      </Radio.Group>
                    )}
                  </Form.Item>
                </div>

                <FormDivider orientation="left" orientationMargin="0">
                  Address Information
                </FormDivider>

                <AddressSection>
                  <div>
                    <Title level={5} style={{ marginBottom: 16 }}>
                      7. Present Address
                    </Title>
                    <Form.Item
                      name="presentAddress"
                      rules={[
                        {
                          required: true,
                          message: "Please enter present address",
                        },
                        {
                          pattern: /^[A-Za-z0-9\s.,\/\-()#&]+$/,
                          message:
                            "Please enter a valid address (alphanumeric with spaces and basic punctuation)",
                        },
                      ]}
                    >
                      {renderField(
                        1,
                        <IconTextArea
                          placeholder="Present Address"
                          prefix={<FaHome />}
                          rows={3}
                        />
                      )}
                    </Form.Item>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "16px",
                      }}
                    >
                      <Form.Item
                        name="presentPin"
                        label="Pin Code"
                        rules={[
                          { required: true, message: "Please enter pincode" },
                          {
                            pattern: /^[1-9][0-9]{5}$/,
                            message:
                              "Please enter a valid 6-digit Indian pincode",
                          },
                        ]}
                      >
                        {renderField(1, <FormInput placeholder="Pin Code" />)}
                      </Form.Item>
                      <Form.Item
                        name="email"
                        label="Email Address"
                        rules={[
                          { required: true, message: "Please enter email" },
                          {
                            pattern:
                              /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                            message: "Please enter a valid email address",
                          },
                        ]}
                      >
                        {renderField(
                          1,
                          <IconInput
                            placeholder="Email"
                            prefix={<FaEnvelope />}
                          />
                        )}
                      </Form.Item>
                    </div>
                  </div>

                  <div>
                    <Title level={5} style={{ marginBottom: 16 }}>
                      8. Permanent Address
                    </Title>
                    <Form.Item>
                      <Checkbox
                        checked={sameAsCurrent}
                        onChange={(e) => setSameAsCurrent(e.target.checked)}
                        disabled={isAllSectionsCompleted()}
                      >
                        Same as Present Address
                      </Checkbox>
                    </Form.Item>
                    <Form.Item
                      name="permanentAddress"
                      rules={[
                        {
                          required: !sameAsCurrent,
                          message: "Please enter permanent address",
                        },
                        {
                          pattern: /^[A-Za-z0-9\s.,\/\-()#&]+$/,
                          message:
                            "Please enter a valid address (alphanumeric with spaces and basic punctuation)",
                        },
                      ]}
                    >
                      {renderField(
                        1,
                        <IconTextArea
                          placeholder="Permanent Address"
                          prefix={<FaHome />}
                          rows={3}
                          disabled={sameAsCurrent || isAllSectionsCompleted()}
                        />
                      )}
                    </Form.Item>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "16px",
                      }}
                    >
                      <Form.Item
                        name="permanentPin"
                        label="Pin Code"
                        rules={[
                          {
                            required: !sameAsCurrent,
                            message: "Please enter pincode",
                          },
                          {
                            pattern: /^[1-9][0-9]{5}$/,
                            message:
                              "Please enter a valid 6-digit Indian pincode",
                          },
                        ]}
                      >
                        {renderField(
                          1,
                          <FormInput
                            placeholder="Pin Code"
                            disabled={sameAsCurrent || isAllSectionsCompleted()}
                          />
                        )}
                      </Form.Item>
                      <Form.Item
                        name="permanentEmail"
                        label="Email Address"
                        rules={[
                          {
                            required: !sameAsCurrent,
                            message: "Please enter email",
                          },
                          {
                            pattern:
                              /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                            message: "Please enter a valid email address",
                          },
                        ]}
                      >
                        {renderField(
                          1,
                          <IconInput
                            placeholder="Email"
                            prefix={<FaEnvelope />}
                            disabled={sameAsCurrent || isAllSectionsCompleted()}
                          />
                        )}
                      </Form.Item>
                    </div>
                  </div>

                  <Form.Item
                    label="Name & Address of employer (Please add your previous employer name & address. In case of fresher, keep it blank.)"
                    name="nameAddressEmployer"
                  >
                    {renderField(
                      1,
                      <Input.TextArea
                        placeholder="Name & Address of employer"
                        autoSize={{ minRows: 3 }}
                      />
                    )}
                  </Form.Item>

                </AddressSection>

                {!isAllSectionsCompleted() && (
                  <Form.Item>
                    <SubmitButton type="primary" htmlType="submit">
                      {isSectionCompleted(1)
                        ? "Update Person's Particulars"
                        : "Save Person's Particulars"}
                    </SubmitButton>
                  </Form.Item>
                )}
              </SectionForm>
            </Panel>
            
            {/* NEW SECTION 2: Employer Details */}
            <Panel
              header={
                <PanelHeader>
                  <PanelIcon>
                    <FaBriefcase />
                  </PanelIcon>
                  <span>Employer Details</span>
                  {isSectionCompleted(2) && (
                    <Text type="success" style={{ marginLeft: "auto" }}>
                      Completed
                    </Text>
                  )}
                </PanelHeader>
              }
              key="2"
            >
              <SectionForm
                form={section2Form}
                layout="vertical"
                onFinish={(values) => handleSectionSubmit(2, values)}
                autoComplete="off"
              >
                <FormDivider orientation="left" orientationMargin="0">
                  Current Employer Information
                </FormDivider>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                  }}
                >
                  <Form.Item
                    label="1. Employer Code No."
                    name="employerCode"
                    rules={[
                      {
                        required: true,
                        message: "Please enter employer code",
                      },
                      {
                        pattern: /^[A-Za-z0-9\-]+$/,
                        message: "Please enter a valid employer code",
                      },
                    ]}
                  >
                    {renderField(
                      2,
                      <IconInput
                        placeholder="Employer Code"
                        prefix={<FaIdCard />}
                      />
                    )}
                  </Form.Item>

                  <Form.Item
                    label="2. Date of Appointment"
                    name="appointmentDate"
                    rules={[
                      {
                        required: true,
                        message: "Please select appointment date",
                      },
                    ]}
                  >
                    {renderField(
                      2,
                      <DatePicker
                        style={{ width: "100%" }}
                        format="DD/MM/YYYY"
                        placeholder="DD/MM/YYYY"
                        disabledDate={disabledDate}
                      />
                    )}
                  </Form.Item>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gap: "16px",
                  }}
                >
                  <Form.Item
                    label="3. Name of Employer"
                    name="employerName"
                    rules={[
                      {
                        required: true,
                        message: "Please enter employer name",
                      },
                    ]}
                  >
                    {renderField(
                      2,
                      <IconInput
                        placeholder="Employer Name"
                        prefix={<FaBuilding />}
                      />
                    )}
                  </Form.Item>

                  <Form.Item
                    label="4. Address of Employer"
                    name="employerAddress"
                    rules={[
                      {
                        required: true,
                        message: "Please enter employer address",
                      },
                    ]}
                  >
                    {renderField(
                      2,
                      <IconTextArea
                        placeholder="Employer Address"
                        prefix={<FaHome />}
                        rows={3}
                      />
                    )}
                  </Form.Item>
                </div>

                <FormDivider orientation="left" orientationMargin="0">
                  Previous Employer Information
                </FormDivider>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                  }}
                >
                  <Form.Item
                    label="5. Previous Employer Code No."
                    name="previousEmployerCode"
                  >
                    {renderField(
                      2,
                      <IconInput
                        placeholder="Previous Employer Code"
                        prefix={<FaIdCard />}
                      />
                    )}
                  </Form.Item>

                  <Form.Item
                    label="6. Previous IP No."
                    name="previousInsNo"
                  >
                    {renderField(
                      2,
                      <IconInput
                        placeholder="Previous Insurance Number"
                        prefix={<FaIdCard />}
                      />
                    )}
                  </Form.Item>
                </div>

                {!isAllSectionsCompleted() && (
                  <Form.Item>
                    <SubmitButton type="primary" htmlType="submit">
                      {isSectionCompleted(2)
                        ? "Update Employer Details"
                        : "Save Employer Details"}
                    </SubmitButton>
                  </Form.Item>
                )}
              </SectionForm>
            </Panel>

            {/* Section 3: Nominee Details */}
            <Panel
              header={
                <PanelHeader>
                  <PanelIcon>
                    <FaUserTie />
                  </PanelIcon>
                  <span>Nominee Details</span>
                  {isSectionCompleted(3) && (
                    <Text type="success" style={{ marginLeft: "auto" }}>
                      Completed
                    </Text>
                  )}
                </PanelHeader>
              }
              key="3"
            >
              <SectionForm
                form={section3Form}
                layout="vertical"
                onFinish={(values) => handleSectionSubmit(3, values)}
                autoComplete="off"
              >
                <Text
                  type="secondary"
                  style={{ display: "block", marginBottom: 16 }}
                >
                  u/s 71 of ESI Act 1948 / Rule 56(2) of ESI (Central) Rules
                  1950 for payment of cash benefit in the event of death
                </Text>

                {(maritalStatus === "U" || maritalStatus === "W") && (
                  <Form.Item>
                    <Checkbox
                      checked={parentsDeceased}
                      onChange={(e) => setParentsDeceased(e.target.checked)}
                      disabled={isAllSectionsCompleted()}
                    >
                      My parents are deceased (Affidavit and death certificate
                      will be required)
                    </Checkbox>
                  </Form.Item>
                )}

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: "16px",
                  }}
                >
                  <Form.Item
                    label="Name of the Nominee (As Per Aadhaar)"
                    name="nomineeName"
                    rules={[
                      { required: true, message: "Please enter nominee name" },
                      {
                        pattern: /^[A-Za-z\s.\-']+$/,
                        message:
                          "Please enter a valid name (only letters, spaces, dots, hyphens and apostrophes)",
                      },
                    ]}
                  >
                    {renderField(
                      3,
                      <FormInput placeholder="Nominee Name (As Per Aadhaar)" />
                    )}
                  </Form.Item>

                  <Form.Item
                    label="Relationship with insured person"
                    name="nomineeRelationship"
                    rules={[
                      { required: true, message: "Please select relationship" },
                    ]}
                  >
                    {renderField(
                      3,
                      <Select
                        placeholder="Select relationship"
                        options={getRelationshipOptions()}
                      />
                    )}
                  </Form.Item>

                  <Form.Item
                    label="Address"
                    name="nomineeAddress"
                    rules={[
                      { required: true, message: "Please enter address" },
                      {
                        pattern: /^[A-Za-z0-9\s.,\/\-()#&]+$/,
                        message:
                          "Please enter a valid address (alphanumeric with spaces and basic punctuation)",
                      },
                    ]}
                  >
                    {renderField(3, <FormInput placeholder="Address" />)}
                  </Form.Item>
                </div>

                {maritalStatus !== "U" && (
                  <Form.Item
                    name="isDependent"
                    valuePropName="checked"
                    rules={[
                      {
                        required: maritalStatus !== "U",
                        message:
                          "Please confirm if your parents are dependent on you",
                      },
                    ]}
                  >
                    {renderField(
                      3,
                      <Checkbox>
                        I declare that my parents are dependent on me
                      </Checkbox>
                    )}
                  </Form.Item>
                )}

                <Form.Item
                  name="declaration"
                  valuePropName="checked"
                  rules={[
                    {
                      required: true,
                      message: "You must accept the declaration to proceed",
                    },
                    {
                      validator: (_, value) =>
                        value
                          ? Promise.resolve()
                          : Promise.reject("You must accept the declaration"),
                    },
                  ]}
                >
                  {renderField(
                    3,
                    <Checkbox>
                      I hereby declare that the above particulars have been
                      given by me and are correct to the best of my knowledge
                      and I belief. I also undertake to intimate to the
                      corporation any change in the membership of my family
                      within 15 days of such change having occurred.
                    </Checkbox>
                  )}
                </Form.Item>

                {!isAllSectionsCompleted() && (
                  <Form.Item>
                    <SubmitButton type="primary" htmlType="submit">
                      {isSectionCompleted(3)
                        ? "Update Nominee Details"
                        : "Save Nominee Details"}
                    </SubmitButton>
                  </Form.Item>
                )}
              </SectionForm>
            </Panel>

            {/* Section 4: Family Particulars */}
            <Panel
              header={
                <PanelHeader>
                  <PanelIcon>
                    <FaUser />
                  </PanelIcon>
                  <span>Family Particulars</span>
                  {isSectionCompleted(4) && (
                    <Text type="success" style={{ marginLeft: "auto" }}>
                      Completed
                    </Text>
                  )}
                </PanelHeader>
              }
              key="4"
            >
              <SectionForm
                form={section4Form}
                layout="vertical"
                onFinish={() => handleSectionSubmit(4, { familyData })}
                autoComplete="off"
              >
                <Title level={4}>
                  (D) FAMILY PARTICULARS OF INSURED PERSON
                </Title>
                <Text
                  type="danger"
                  style={{ display: "block", marginBottom: 16 }}
                >
                  * Note: At least one family member must be filled
                </Text>

                {maritalStatus === "U" && parentsDeceased && (
                  <Text
                    type="warning"
                    style={{ display: "block", marginBottom: 16 }}
                  >
                    Note: As you have declared your parents as deceased, you can
                    only add siblings as family members.
                  </Text>
                )}

                {maritalStatus !== "U" && (
                  <Text
                    type="secondary"
                    style={{ display: "block", marginBottom: 16 }}
                  >
                    Note: Married employees can add spouse and children. Parents
                    can only be added if they are dependent.
                  </Text>
                )}

                <StyledTable
                  columns={familyColumns}
                  dataSource={familyData}
                  pagination={false}
                  bordered
                  size="small"
                />

                {!isAllSectionsCompleted() && (
                  <Form.Item>
                    <SubmitButton type="primary" htmlType="submit">
                      {isSectionCompleted(4)
                        ? "Update Family Particulars"
                        : "Save Family Particulars"}
                    </SubmitButton>
                  </Form.Item>
                )}
              </SectionForm>
            </Panel>

            {/* Section 5: Temporary Identity Card */}
            <Panel
              header={
                <PanelHeader>
                  <PanelIcon>
                    <FaIdCard />
                  </PanelIcon>
                  <span>Temporary Identity Card</span>
                  {isSectionCompleted(5) && (
                    <Text type="success" style={{ marginLeft: "auto" }}>
                      Completed
                    </Text>
                  )}
                </PanelHeader>
              }
              key="5"
            >
              <SectionForm
                form={section5Form}
                layout="vertical"
                onFinish={handleSection5Submit}
                autoComplete="off"
              >
                <div style={{ textAlign: "center", marginBottom: 16 }}>
                  <Title level={4} style={{ margin: 0 }}>
                    ESI CORPORATION
                  </Title>
                  <Title level={5} style={{ margin: "8px 0 16px" }}>
                    Temporary Identity Card
                  </Title>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    margin: "24px 0",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <PreviewModal
                    visible={showPreviewModal}
                    image={previewImage || mainData?.photograph}
                    onCancel={() => setShowPreviewModal(false)}
                  />

                  <div
                    style={{
                      border: "1px solid #e8e8e8",
                      borderRadius: "8px",
                      padding: "24px",
                      width: "100%",
                      maxWidth: "400px",
                      textAlign: "center",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                  >
                    <Text
                      strong
                      style={{
                        display: "block",
                        marginBottom: "16px",
                        fontSize: "16px",
                        color: "#333",
                      }}
                    >
                      Employee Family Photograph
                    </Text>

                    <div
                      style={{
                        width: "150px",
                        height: "180px",
                        border: "1px dashed #d9d9d9",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "#fafafa",
                        margin: "0 auto 16px",
                        position: "relative",
                        overflow: "hidden",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        (previewImage || mainData?.photograph) &&
                        setShowPreviewModal(true)
                      }
                    >
                      {previewImage ? (
                        <>
                          <img
                            src={previewImage}
                            alt="preview"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                          <div
                            style={{
                              position: "absolute",
                              bottom: 0,
                              left: 0,
                              right: 0,
                              background: "rgba(0,0,0,0.5)",
                              color: "white",
                              padding: "4px",
                              fontSize: "12px",
                              textAlign: "center",
                            }}
                          >
                            Click to enlarge
                          </div>
                        </>
                      ) : mainData?.photograph ? (
                        <>
                          <img
                            src={mainData.photograph}
                            alt="employee-photo"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                          <div
                            style={{
                              position: "absolute",
                              bottom: 0,
                              left: 0,
                              right: 0,
                              background: "rgba(0,0,0,0.5)",
                              color: "white",
                              padding: "4px",
                              fontSize: "12px",
                              textAlign: "center",
                            }}
                          >
                            Click to enlarge
                          </div>
                        </>
                      ) : (
                        <div
                          style={{
                            color: "#999",
                            textAlign: "center",
                            padding: "16px",
                          }}
                        >
                          <CameraOutlined
                            style={{ fontSize: "24px", marginBottom: "8px" }}
                          />
                          <div>No photo selected</div>
                        </div>
                      )}
                    </div>

                    <div
                      style={{
                        marginBottom: "16px",
                        display: "flex",
                        justifyContent: "center",
                        gap: "8px",
                      }}
                    >
                      <Form.Item
                        name="photograph"
                        valuePropName="fileList"
                        getValueFromEvent={normFile}
                        noStyle
                      >
                    <Upload
  name="photograph"
  accept="image/jpeg,image/jpg,image/png,image/jfif"  // Updated accept attribute
  beforeUpload={(file) => {
    // Check file type
    const allowedTypes = [
      'image/jpeg',
      'image/jpg', 
      'image/png',
      'image/jfif'
    ];
    
    // Also check file extension as a fallback
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'jfif'];
    const fileExtension = file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      message.error('You can only upload JPG, JPEG, PNG, or JFIF files!');
      return false;
    }
    
    // Check file size (2MB)
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('Image must be smaller than 2MB!');
      return false;
    }
    
    setPreviewImage(URL.createObjectURL(file));
    setSelectedFile(file);
    return false; // Prevent auto upload
  }}
  showUploadList={false}
>
  <Button type="default" icon={<UploadOutlined />}>
    {mainData?.photograph ? "Change Photo" : "Select Photo"}
  </Button>
</Upload>
                      </Form.Item>

                      {mainData?.photograph && !selectedFile && (
                        <Button
                          icon={<EyeOutlined />}
                          onClick={() => setShowPreviewModal(true)}
                        >
                          View Uploaded
                        </Button>
                      )}

                      {selectedFile && (
                        <Button
                          type="primary"
                          onClick={handleUpload}
                          loading={uploading}
                          icon={<CloudUploadOutlined />}
                        >
                          {uploading ? "Uploading..." : "Upload Photo"}
                        </Button>
                      )}
                    </div>

                 <Text type="secondary" style={{ fontSize: "12px", color: "#999" }}>
  Passport size photo (JPG, JPEG, PNG, JFIF formats only, 2MB max)
</Text>
                  </div>
                </div>

                <div
                  style={{
                    border: "1px solid #d9d9d9",
                    padding: 16,
                    marginBottom: 16,
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                    }}
                  >
                    {/* <Form.Item
                      label="Name (As Per Aadhaar)"
                      name="name"
                      initialValue={mainData?.section1?.name || ""}
                      rules={[
                        { required: true, message: "Please enter name" },
                        {
                          pattern: /^[A-Za-z\s.\-']+$/,
                          message:
                            "Please enter a valid name (only letters, spaces, dots, hyphens and apostrophes)",
                        },
                      ]}
                    >
                      {renderField(
                        5,
                        <FormInput
                          value={mainData?.section1?.name || ""}
                          readOnly
                        />
                      )}
                    </Form.Item> */}
                    <Form.Item
                      label="Date of Entry"
                      name="entryDate"
                      rules={[
                        {
                          required: true,
                          message: "Please select date of entry",
                        },
                      ]}
                    >
                      {renderField(
                        5,
                        <DatePicker
                          style={{ width: "100%" }}
                          format="DD/MM/YYYY"
                          placeholder="DD/MM/YYYY"
                         
                        />
                      )}
                    </Form.Item>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                    }}
                  >
                    <Form.Item
                      label="Ins. No"
                      name="cardInsNo"
                      rules={[
                        {
                          pattern: /^[A-Za-z0-9\/\-]+$/,
                          message:
                            "Please enter a valid insurance number (alphanumeric with / or -)",
                        },
                      ]}
                    >
                      {renderField(
                        5,
                        <FormInput
                          
                        />
                      )}
                    </Form.Item>

                    {/* <Form.Item
                      label="Father's/Husband's Name (As Per Aadhaar)"
                      name="fatherHusbandName"
                      initialValue={mainData?.section1?.fatherHusbandName || ""}
                      rules={[
                        {
                          required: true,
                          message: "Please enter father/husband name",
                        },
                        {
                          pattern: /^[A-Za-z\s.\-']+$/,
                          message:
                            "Please enter a valid name (only letters, spaces, dots, hyphens and apostrophes)",
                        },
                      ]}
                    >
                      {renderField(5, <FormInput />)}
                    </Form.Item> */}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "16px",
                        width: "50%",
                      }}
                    >
                      <Form.Item
                        label="Dated"
                        name="cardDated"
                        initialValue={moment()}
                        defaultValue={moment()}
                        rules={[
                          { required: true, message: "Please select date" },
                        ]}
                      >
                        {renderField(
                          5,
                          <DatePicker
                            style={{ width: "100%" }}
                            format="DD/MM/YYYY"
                            placeholder="DD/MM/YYYY"
                            disabledDate={disabledDate}
                           
                          />
                        )}
                      </Form.Item>
                    </div>
                  </div>
                </div>

                {!isAllSectionsCompleted() && (
                  <Form.Item>
                      {!mainData?.photograph && !photoUploaded && (
                          <Text type="danger" style={{ display: 'block', marginBottom: 16 }}>
                              Please upload a photograph before submitting
                          </Text>
                      )}
                      <SubmitButton
                          type="primary"
                          htmlType="submit"
                          disabled={!mainData?.photograph && !photoUploaded}
                      >
                          {isSectionCompleted(5) ? 'Update Temporary Identity Card' : 'Save Temporary Identity Card'}
                      </SubmitButton>
                  </Form.Item>
                )}
              </SectionForm>
            </Panel>
          </CustomCollapse>
        </MainCard>
      </EmployeesContainer>
    );
  }
);

export default ESIForm;